import React, { useState, useRef, useEffect, Suspense } from 'react'
const LazyMonacoEditor = React.lazy(() => import('@monaco-editor/react'))
import { 
  Copy, 
  Upload, 
  RotateCcw, 
  CheckCircle, 
  AlertCircle, 
  XCircle,
  Info,
  ChevronDown,
  Sun,
  Moon,
  Clipboard,
  GitBranch
} from 'lucide-react'
import { useJsonEditor } from '../hooks/useJsonEditor'
import JsonTreeVisualizer from './JsonTreeVisualizer'
import './JsonEditor.css'

const JsonEditor: React.FC = () => {
  const {
    state,
    fileInputRef,
    updateJsonContent,
    handleFormat,
    handleMinify,
    handleCopy,
    handleUpload,
    handleClear,
    handleLoadSample,
    handlePaste,
    toggleValidationPanel,
    expandValidationPanel,
    toggleTheme,
    toggleTreeVisualizer,
    hasIssues
  } = useJsonEditor()

  const [showClipboardDialog, setShowClipboardDialog] = useState<boolean>(false)
  const [clipboardError, setClipboardError] = useState<string>('')
  const editorRef = useRef<any>(null)
  
  // Panel resizing state - persists during session, resets on page reload
  const [editorWidth, setEditorWidth] = useState<number>(50) // Default to 50%
  const [isResizing, setIsResizing] = useState<boolean>(false)
  const separatorRef = useRef<HTMLDivElement>(null)

  // Update editor width (no persistence needed - just state)
  const updateEditorWidth = (width: number) => {
    setEditorWidth(width)
  }

  // Handle mouse events for resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !state.isTreeVisualizerVisible) return
      
      const container = document.querySelector('.editor-container.with-tree-view')
      if (!container) return
      
      const rect = container.getBoundingClientRect()
      const newEditorWidth = ((e.clientX - rect.left) / rect.width) * 100
      
      // Limit the editor width between 20% and 80% (tree panel takes 20% to 80% space)
      const clampedWidth = Math.max(20, Math.min(80, newEditorWidth))
      updateEditorWidth(clampedWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.body.style.cursor = 'default'
      document.body.style.userSelect = 'auto'
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'default'
      document.body.style.userSelect = 'auto'
    }
  }, [isResizing, state.isTreeVisualizerVisible])

  const handleSeparatorMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }

  // Handle tree visualizer toggle (width persists in state during session)
  const handleTreeVisualizerToggle = () => {
    toggleTreeVisualizer()
  }

  // No need to restore width - it's already in state and persists during session

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Handle Ctrl+V or Cmd+V for paste
      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        // Only handle if the editor is focused
        if (editorRef.current && editorRef.current.hasTextFocus()) {
          // Let the editor handle the paste naturally
          return
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleEditorChange = (value: string | undefined) => {
    try {
      updateJsonContent(value || '')
    } catch (error) {
      console.error('Failed to update content:', error)
    }
  }

  const onFormat = () => {
    try {
      handleFormat()
    } catch (error) {
      console.error('Formatting failed:', error)
    }
  }

  const onMinify = () => {
    try {
      handleMinify()
    } catch (error) {
      console.error('Minification failed:', error)
    }
  }

  const onCopy = async () => {
    try {
      handleCopy()
    } catch (error) {
      console.error('Copy failed:', error)
    }
  }



  const onUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    
    if (file) {
      try {
        handleUpload(event)
      } catch (error) {
        console.error('Upload failed:', error)
      }
    }
  }

  const onClear = () => {
    try {
      handleClear()
    } catch (error) {
      console.error('Clear failed:', error)
    }
  }

  const onLoadSample = () => {
    try {
      handleLoadSample()
    } catch (error) {
      console.error('Load sample failed:', error)
    }
  }

  const onPaste = async () => {
    // Check if Clipboard API is supported
    if (!navigator.clipboard) {
      setClipboardError('Clipboard API is not supported in this browser. Please use Ctrl+V (or Cmd+V) to paste.')
      setShowClipboardDialog(true)
      return
    }

    try {
      // Request clipboard permission and read text
      const text = await navigator.clipboard.readText()
      if (text) {
        handlePaste(text)
      }
    } catch (err: any) {
      console.error('Failed to paste from clipboard: ', err)
      
      // Handle specific error cases
      if (err.name === 'NotAllowedError') {
        setClipboardError('Clipboard permission denied. Please allow clipboard access or use Ctrl+V (or Cmd+V) to paste.')
      } else if (err.name === 'SecurityError') {
        setClipboardError('Clipboard access requires HTTPS. Please use Ctrl+V (or Cmd+V) to paste.')
      } else {
        setClipboardError('Failed to access clipboard. Please use Ctrl+V (or Cmd+V) to paste.')
      }
      setShowClipboardDialog(true)
    }
  }

  const closeClipboardDialog = () => {
    setShowClipboardDialog(false)
    setClipboardError('')
  }

  const onToggleValidationPanel = () => {
    try {
      toggleValidationPanel()
    } catch (error) {
      console.error('Toggle validation panel failed:', error)
    }
  }

  const onExpandValidationPanel = () => {
    try {
      expandValidationPanel()
    } catch (error) {
      console.error('Expand validation panel failed:', error)
    }
  }

  const onToggleTheme = () => {
    try {
      toggleTheme()
    } catch (error) {
      console.error('Toggle theme failed:', error)
    }
  }

  const getStatusIcon = () => {
    if (state.validationResult.errors.length > 0) {
      return <XCircle className="status-icon error" />
    } else if (state.validationResult.warnings.length > 0) {
      return <AlertCircle className="status-icon warning" />
    } else if (state.jsonContent.trim()) {
      return <CheckCircle className="status-icon success" />
    }
    return <Info className="status-icon info" />
  }

  const getStatusText = () => {
    if (state.validationResult.errors.length > 0) {
      return `${state.validationResult.errors.length} error(s)`
    } else if (state.validationResult.warnings.length > 0) {
      return `${state.validationResult.warnings.length} warning(s)`
    } else if (state.jsonContent.trim()) {
      return 'Valid JSON'
    }
    return 'Ready to edit'
  }

  const getValidationSummary = () => {
    const totalIssues = state.validationResult.errors.length + state.validationResult.warnings.length
    if (totalIssues === 0) return 'No issues found'
    
    const parts = []
    if (state.validationResult.errors.length > 0) {
      parts.push(`${state.validationResult.errors.length} error${state.validationResult.errors.length > 1 ? 's' : ''}`)
    }
    if (state.validationResult.warnings.length > 0) {
      parts.push(`${state.validationResult.warnings.length} warning${state.validationResult.warnings.length > 1 ? 's' : ''}`)
    }
    return parts.join(', ')
  }



  return (
    <div className="json-editor">
      <div className="editor-header">
        <div className="editor-controls">
          <button 
            onClick={onFormat}
            className="control-btn primary"
            disabled={!state.jsonContent.trim()}
          >
            <RotateCcw size={16} />
            Format
          </button>
          <button 
            onClick={onMinify}
            className="control-btn"
            disabled={!state.jsonContent.trim()}
          >
            Minify
          </button>
          <button 
            onClick={onCopy}
            className="control-btn"
            disabled={!state.jsonContent.trim()}
          >
            <Copy size={16} />
            {state.showCopied ? 'Copied!' : 'Copy'}
          </button>

          <button 
            onClick={onPaste}
            className="control-btn"
            title="Paste from clipboard (or use Ctrl+V / Cmd+V)"
          >
            <Clipboard size={16} />
            Paste
          </button>
          <button 
            onClick={() => fileInputRef.current?.click()}
            className="control-btn"
          >
            <Upload size={16} />
            Upload
          </button>
          <button 
            onClick={onClear}
            className="control-btn"
            disabled={!state.jsonContent.trim()}
          >
            Clear
          </button>
          <button 
            onClick={onLoadSample}
            className="control-btn"
          >
            Load Sample
          </button>
          <button 
            onClick={onToggleTheme}
            className="control-btn theme-toggle"
            title={state.isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {state.isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
            {state.isDarkMode ? 'Light' : 'Dark'}
          </button>
        </div>
        
        <div className="editor-status-container">
          <div 
            className={`editor-status ${hasIssues ? 'clickable' : ''}`}
            onClick={hasIssues ? onExpandValidationPanel : undefined}
            title={hasIssues ? 'Click to view details' : undefined}
          >
            {getStatusIcon()}
            <span className="status-text">{getStatusText()}</span>
          </div>
          
          <button 
            onClick={handleTreeVisualizerToggle}
            className={`tree-view-toggle ${state.isTreeVisualizerVisible ? 'active' : ''}`}
            title="Toggle JSON Tree View"
          >
            <GitBranch size={16} />
          </button>
        </div>
      </div>

      <div className={`editor-container ${state.isTreeVisualizerVisible ? 'with-tree-view' : ''}`}>
        <div 
          className="editor-main"
          style={state.isTreeVisualizerVisible ? { flex: `0 0 ${editorWidth}%` } : {}}
        >
          <Suspense fallback={<div className="editor-loading">Loading editor…</div>}>
          <LazyMonacoEditor
            height="100%"
            defaultLanguage="json"
            value={state.jsonContent}
            onChange={handleEditorChange}
            onMount={(editor) => {
              editorRef.current = editor
              
              // Fix scroll behavior to prevent ghost lines
              setTimeout(() => {
                try {
                  // Force scroll to top to reset any unwanted scroll position
                  editor.revealLine(1, 0)
                  editor.setPosition({ lineNumber: 1, column: 1 })
                  
                  // Disable any automatic scrolling
                  const model = editor.getModel()
                  if (model) {
                    model.setEOL(1) // Set end of line to LF
                  }
                } catch (error) {
                  console.warn('Could not fix scroll behavior:', error)
                }
              }, 100)
            }}
            theme={state.isDarkMode ? 'vs-dark' : 'vs-light'}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              roundedSelection: false,
              scrollBeyondLastLine: false,
              scrollBeyondLastColumn: 0,
              automaticLayout: true,
              wordWrap: 'on',
              folding: true,
              foldingStrategy: 'indentation',
              showFoldingControls: 'always',
              renderWhitespace: 'selection',
              tabSize: 2,
              insertSpaces: true,
              detectIndentation: false,
              trimAutoWhitespace: true,
              largeFileOptimizations: true,
              fixedOverflowWidgets: true,
              smoothScrolling: true,
              scrollbar: {
                vertical: 'visible',
                horizontal: 'visible',
                verticalScrollbarSize: 14,
                horizontalScrollbarSize: 14,
                useShadows: false,
                verticalHasArrows: false,
                horizontalHasArrows: false
              },
              suggest: {
                showKeywords: false,
                showSnippets: false,
                showClasses: false,
                showFunctions: false,
                showVariables: false,
                showConstants: false,
                showEnums: false,
                showModules: false,
                showProperties: false,
                showEvents: false,
                showOperators: false,
                showUnits: false,
                showValues: false,
                showColors: false,
                showFiles: false,
                showReferences: false,
                showFolders: false,
                showTypeParameters: false,
                showWords: false,
                showUsers: false,
                showIssues: false,
              }
            }}
          />
          </Suspense>
        </div>
        
        {state.isTreeVisualizerVisible && (
          <>
            <div 
              ref={separatorRef}
              className="panel-separator"
              onMouseDown={handleSeparatorMouseDown}
              title="Drag to resize panels"
            />
                         <JsonTreeVisualizer
               jsonData={(() => {
                 try {
                   return state.jsonContent ? JSON.parse(state.jsonContent) : null
                 } catch {
                   return null
                 }
               })()}
               isVisible={state.isTreeVisualizerVisible}
               onClose={handleTreeVisualizerToggle}
               isDarkMode={state.isDarkMode}
             />
          </>
        )}
      </div>

      {hasIssues && (
        <div className={`validation-panel ${state.isValidationPanelCollapsed ? 'collapsed' : ''} ${state.validationResult.errors.length > 0 ? 'has-errors' : ''}`}>
          <div className="validation-header" onClick={onToggleValidationPanel}>
            <div className="validation-header-title">
              {getStatusIcon()}
              <span>{getValidationSummary()}</span>
            </div>
            <div className={`validation-toggle ${!state.isValidationPanelCollapsed ? 'expanded' : ''}`}>
              <ChevronDown size={16} />
            </div>
          </div>
          
          <div className="validation-content">
            {state.validationResult.errors.length > 0 && (
              <div className="validation-section">
                <h3 className="validation-title error">
                  <XCircle size={14} />
                  Errors ({state.validationResult.errors.length})
                </h3>
                <ul className="validation-list">
                  {state.validationResult.errors.map((error: string, index: number) => (
                    <li key={index} className="validation-item error">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {state.validationResult.warnings.length > 0 && (
              <div className="validation-section">
                <h3 className="validation-title warning">
                  <AlertCircle size={14} />
                  Warnings ({state.validationResult.warnings.length})
                </h3>
                <ul className="validation-list">
                  {state.validationResult.warnings.map((warning: string, index: number) => (
                    <li key={index} className="validation-item warning">
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={onUpload}
        style={{ display: 'none' }}
      />

      {/* Clipboard Error Dialog */}
      {showClipboardDialog && (
        <div className="clipboard-dialog-overlay">
          <div className="clipboard-dialog">
            <div className="clipboard-dialog-header">
              <h3>Clipboard Access Issue</h3>
              <button 
                onClick={closeClipboardDialog}
                className="clipboard-dialog-close"
                title="Close"
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="clipboard-dialog-content">
              <p>{clipboardError}</p>
              <div className="clipboard-dialog-actions">
                <button 
                  onClick={closeClipboardDialog}
                  className="clipboard-dialog-btn primary"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default JsonEditor
